import { ArrowLeft, ArrowUpRight, Compass, TrainFront, Lightbulb, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { ShareButton } from "@/components/ShareButton";
import { GygAvailability, GygTourBand } from "@/components/GetYourGuide";

const SITE = "https://cphhighlights.com";
const IMG = (id) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400`;

const FRAMING = {
  both: { label: "DAY TRIP OR OVERNIGHT", color: "#E8590C" },
  day: { label: "LONG DAY TRIP", color: "#0E7C5A" },
  overnight: { label: "WORTH AN OVERNIGHT", color: "#1E3A8A" },
};

const GEMS = [
  {
    name: "Odense",
    tag: "H.C. Andersen's hometown",
    meta: "1h 50min by train",
    mins: 110,
    framing: "both",
    image: IMG("1693329048681-409f68fccaa6"),
    blurb: (
      <>
        Denmark&apos;s third city is where{" "}
        <Link
          to="/hc-andersen"
          className="text-[#DF3226] font-700 underline underline-offset-2 decoration-[#DF3226]/40 hover:decoration-[#DF3226]"
        >
          Hans Christian Andersen
        </Link>{" "}
        was born — and it wears it well. The cobbled old town around his childhood home is all
        crooked yellow houses, and the brand-new H.C. Andersens Hus is one of the most imaginative
        museums in the country.
      </>
    ),
    highlights: [
      "H.C. Andersens Hus — the stunning new museum built around his fairy tales",
      "The old town — cobbled lanes and yellow half-timbered houses",
      "The Funen Village — an open-air museum of 18th-century farm life",
      "A lazy riverboat ride on the Odense Å",
    ],
    gettingThere: "Direct trains from København H run every 30 minutes — about 1h 50min each way.",
    tip: "The Andersen museum is the stop. Book tickets online in summer, and walk the old town before or after.",
    tours: [
      { id: "1339500", label: "Odense from Copenhagen — 2 days, bus and hotel included" },
    ],
  },
  {
    name: "Aarhus",
    tag: "The second city",
    meta: "3h by train",
    mins: 180,
    framing: "overnight",
    image: IMG("1761403836425-78516eb59d8b"),
    blurb:
      "Denmark's second city packs a serious punch: the rainbow panorama on top of ARoS, a whole recreated old town at Den Gamle By, and a café-filled Latin Quarter that feels like a smaller, softer Copenhagen.",
    highlights: [
      "ARoS — walk inside Your Rainbow Panorama above the city",
      "Den Gamle By — the open-air museum of Danish town life through the centuries",
      "The Latin Quarter — the city's prettiest streets and cafés",
      "Moesgaard Museum — world-class prehistory in a grass-covered roof you can walk on",
    ],
    gettingThere: "Direct trains leave København H roughly hourly — about 3 hours. Take the early one, or stay the night.",
    tip: "ARoS plus Den Gamle By fills a day. Add Moesgaard and the harbour baths if you stay over.",
    tours: [{ id: "1070063", label: "Guided harbour cruise — see Aarhus from the water" }],
  },
  {
    name: "Skagen",
    tag: "The top of Denmark",
    meta: "6h by train · easier by car",
    mins: 360,
    framing: "overnight",
    image: IMG("1726599455017-35428c513afa"),
    blurb:
      "At Denmark's northern tip, two seas crash into each other at Grenen, and the light is so famous a whole school of painters — the Skagen Painters — moved here for it. Yellow houses, red roofs, white sand.",
    highlights: [
      "Grenen — stand with one foot in each of two seas",
      "Skagens Museum — the famous Skagen Painters' works",
      "Råbjerg Mile — a migrating sand dune you can walk across",
      "The harbour — fish straight off the boats in the old warehouses",
    ],
    gettingThere: "Train via Frederikshavn takes about 6 hours, or drive. This one is a proper weekend, not a day trip.",
    tip: "Go in summer for the endless light — and book accommodation early, Skagen sells out.",
  },
  {
    name: "Billund",
    tag: "Legoland & the LEGO House",
    meta: "~3h by train + bus · or a 45-min flight",
    mins: 185,
    framing: "overnight",
    image: IMG("1653818627392-6ab73f46bcf2"),
    blurb:
      "The town where LEGO was born is a full-on family pilgrimage: the original Legoland, the extraordinary LEGO House, and Miniland's tiny cities built from millions of bricks.",
    highlights: [
      "Legoland — the original park, with Miniland's brick-built world",
      "LEGO House — the 'Home of the Brick', part museum, part playground",
      "Lalandia — a huge indoor waterpark next door",
    ],
    gettingThere: "Train to Vejle plus a local bus — about 3 hours in total. Or fly Copenhagen to Billund in 45 minutes.",
    tip: "The LEGO House runs on timed tickets and sells out — book well ahead. Legoland plus LEGO House is really two days.",
    tours: [
      { id: "382808", label: "Legoland 1-day ticket — all rides included" },
    ],
  },
  {
    name: "Møns Klint",
    tag: "The white cliffs",
    meta: "2h 10min by car · 2h 40min train/bus",
    mins: 130,
    framing: "both",
    image: IMG("1782246596905-d527fbfd7baf"),
    blurb:
      "Denmark's most dramatic landscape: 128-metre chalk cliffs dropping into a green-blue sea, with a pebble beach at the bottom where fossils wash up. On a clear night it's also one of the country's best dark-sky spots.",
    highlights: [
      "The cliff-top walk — views straight down the chalk face",
      "The beach — 500 steps down, fossils and flint underfoot",
      "GeoCenter Møns Klint — the story of the cliffs, good for kids",
      "Dark-sky stargazing if you stay into the evening",
    ],
    gettingThere: "About 2h 10min by car, or 2h 40min by train and bus. Organized day tours run from Copenhagen too.",
    tip: "It's roughly 500 steps down to the beach and back up. Wear proper shoes, and go early in summer.",
    tours: [
      { id: "470545", label: "Møns Klint & the Forest Tower — day tour from Copenhagen" },
    ],
  },
  {
    name: "Blåvand & the west coast",
    tag: "Denmark's wild side",
    meta: "~4h by car",
    mins: 240,
    framing: "overnight",
    image: IMG("1787171478328-e58583fe89ce"),
    blurb:
      "The North Sea coast is Denmark at its wildest: endless dune-backed beaches, the Blåvandshuk lighthouse at the westernmost point, and WWII bunkers turned into a world-class museum at Tirpitz.",
    highlights: [
      "Blåvandshuk lighthouse — the westernmost point in Denmark",
      "The beaches — wide, empty and great for amber hunting",
      "Tirpitz — a hidden WWII bunker museum built into the dunes",
      "Dune walks and big-sky sunsets over the North Sea",
    ],
    gettingThere: "Train to Esbjerg plus a local bus, or drive — about 4 hours from Copenhagen. This one's an overnight trip, not a day trip.",
    tip: "Bring a windproof jacket, even in July. The wind is the whole point.",
  },
];

const GEM_FAQ = [
  {
    q: "Can you do Odense as a day trip from Copenhagen?",
    a: "Yes — direct trains take about 1h 50min each way, so Odense works as a long day trip. Aarhus, Skagen, Billund and the west coast are better with an overnight.",
  },
  {
    q: "How do I get to Aarhus from Copenhagen?",
    a: "Direct trains run roughly hourly from København H and take about 3 hours. Take the early train for a very full day, or stay the night and do it properly.",
  },
  {
    q: "Do I need a car for Møns Klint?",
    a: "It helps, but it's doable without — train and bus get you there in about 2h 40min, while driving takes about 2h 10min. Organized day tours run from Copenhagen too. Odense, Aarhus, Skagen and Billund are all easy by public transport.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: GEM_FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Beyond Copenhagen: Odense, Aarhus, Skagen & More Danish Gems",
  url: `${SITE}/beyond-copenhagen`,
  description:
    "The best of Denmark beyond Copenhagen — H.C. Andersen's Odense, Aarhus and its rainbow museum, Skagen's two seas, Legoland in Billund, the white cliffs of Møns Klint and the wild west coast at Blåvand. Train times, tips and what not to miss.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Day trips from Copenhagen", item: `${SITE}/trips` },
    { "@type": "ListItem", position: 3, name: "Beyond Copenhagen", item: `${SITE}/beyond-copenhagen` },
  ],
};

export default function BeyondCopenhagenPage() {
  return (
    <main data-testid="beyond-page" className="bg-white text-black">
      <Seo
        title="Beyond Copenhagen: Odense, Aarhus, Skagen & More Danish Gems"
        description="Denmark beyond Copenhagen: H.C. Andersen's Odense, Aarhus' rainbow museum, Skagen's two seas, Legoland, Møns Klint and wild Blåvand — with train times and tips."
        path="/beyond-copenhagen"
        image={IMG("1761403836425-78516eb59d8b")}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
      </Helmet>

      {/* hero */}
      <section data-testid="beyond-hero" className="relative overflow-hidden bg-[#13141A] text-white border-b border-white/20">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1559070226-d4cad642bede?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxEZW5tYXJrfGVufDB8fHx8MTc5MTQ2MTA3MXww&ixlib=rb-4.1.0&q=85" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-12 md:pb-16">
          <Reveal>
            <div className="flex items-center gap-3 mb-12 md:mb-14">
              <Link
                to="/trips"
                data-testid="beyond-back"
                className="inline-flex items-center gap-2 font-body font-700 text-sm bg-[#DF3226] text-white px-5 py-2.5 rounded-full border border-black shadow-brut hover:-translate-y-0.5 transition-all"
              >
                <ArrowLeft size={16} /> Day trips
              </Link>
              <ShareButton title="Beyond Copenhagen — Danish Gems Worth the Trip" testid="beyond-share" />
            </div>
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white mb-4">
              FURTHER AFIELD
            </p>
            <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Denmark, <span className="font-editorial italic font-600">beyond Copenhagen</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/80 max-w-2xl mt-6">
              The day trips page covers the easy hour-or-less escapes. These are the gems worth a longer
              ride — H.C. Andersen&apos;s Odense, Aarhus&apos; rainbow museum, the top of Denmark at
              Skagen and the wild west coast. Some work as a long day; the rest deserve a night.
            </p>
          </Reveal>
        </div>
      </section>

      {/* destinations */}
      <section data-testid="beyond-list" className="py-12 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 space-y-14 md:space-y-20">
          {[...GEMS].sort((a, b) => a.mins - b.mins).map((g, i) => (
            <Reveal key={g.name} y={30}>
              <article
                data-testid={`beyond-gem-${i}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="border border-zinc-200 shadow-brut overflow-hidden aspect-[16/10]">
                  <img src={g.image} alt={`${g.name}, Denmark`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="font-display font-900 text-[#DF3226] text-lg">0{i + 1}</span>
                    <span
                      className="font-body font-800 text-[11px] tracking-widest text-white px-3 py-1 rounded-full"
                      style={{ backgroundColor: FRAMING[g.framing].color }}
                    >
                      {FRAMING[g.framing].label}
                    </span>
                    <span className="font-body font-700 text-[11px] tracking-widest text-black/50 flex items-center gap-1">
                      <TrainFront size={13} /> {g.meta.toUpperCase()}
                    </span>
                  </div>
                  <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl leading-none">
                    {g.name}
                  </h2>
                  <p className="font-body font-700 text-xs tracking-widest text-[#DF3226] mt-1.5">
                    {g.tag.toUpperCase()}
                  </p>
                  <p className="font-body text-sm md:text-base text-black/70 mt-4 leading-relaxed">
                    {g.blurb}
                  </p>
                  <ul className="mt-4 space-y-1.5">
                    {g.highlights.map((h) => (
                      <li key={h} className="font-body text-sm text-black/80 flex items-start gap-2">
                        <ArrowUpRight size={14} className="shrink-0 mt-0.5 text-[#DF3226]" /> {h}
                      </li>
                    ))}
                  </ul>
                  <p className="font-body text-sm text-black/70 mt-4 flex items-start gap-2">
                    <TrainFront size={15} className="shrink-0 mt-0.5 text-[#DF3226]" /> {g.gettingThere}
                  </p>
                  <p className="font-body text-sm text-black/70 mt-2 flex items-start gap-2">
                    <Lightbulb size={15} className="shrink-0 mt-0.5 text-[#E8590C]" /> {g.tip}
                  </p>
                  {g.tours && g.tours.length > 0 && (
                    <div className="mt-5 space-y-4" data-testid={`beyond-tours-${i}`}>
                      {g.tours.map((t) => (
                        <div key={t.id}>
                          <p className="font-body font-800 text-[11px] tracking-widest text-[#DF3226] mb-2">
                            {t.label.toUpperCase()}
                          </p>
                          <GygAvailability tourId={t.id} variant="horizontal" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* planner CTA */}
      <section data-testid="beyond-cta" className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-24 text-center">
          <Reveal>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">STAYING LOCAL?</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl mt-4 max-w-2xl mx-auto">
              The close-in trips are <span className="font-editorial italic font-600">an hour away</span>
            </h2>
            <p className="font-body text-base text-white/80 max-w-xl mx-auto mt-5">
              Louisiana, Hamlet&apos;s castle, Viking ships at Roskilde and Malmö across the bridge —
              all under an hour from the city centre.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/trips"
                data-testid="beyond-cta-trips"
                className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
              >
                <Star size={18} /> All day trips
              </Link>
              <Link
                to="/#planner"
                data-scroll="#planner"
                data-testid="beyond-cta-planner"
                className="inline-flex items-center gap-2 font-display font-800 text-base border border-white/40 text-white px-6 py-3.5 rounded-full hover:bg-white hover:text-[#DF3226] transition-colors"
              >
                Open the planner
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Lalandia — the waterpark next to Legoland */}
      <GygTourBand tourId="1265646" label="THE WATERPARK NEXT TO LEGOLAND" />

      <ExploreLinks
        testid="explore-beyond"
        links={[
          { label: "Closer to town — day trips from Copenhagen", to: "/trips" },
          { label: "How to get there — the transport guide", to: "/transport" },
          { label: "Back in the city — the 3-day itinerary", to: "/copenhagen-itinerary" },
        ]}
      />

      {/* FAQ — last, before the share bar */}
      <section data-testid="beyond-faq" className="pb-12 md:pb-20 border-t border-dotted border-zinc-300 pt-12 md:pt-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <h2 className="font-display font-900 tracking-tight text-3xl md:text-4xl mb-6">
            Good to know
          </h2>
          <div className="space-y-3">
            {GEM_FAQ.map((f, i) => (
              <details
                key={f.q}
                data-testid={`beyond-faq-${i}`}
                className="group bg-white border border-zinc-200 rounded-xl px-5 py-4"
              >
                <summary className="cursor-pointer list-none font-display font-800 text-base md:text-lg flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[#DF3226] text-xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <ShareBar title="Beyond Copenhagen — Danish Gems Worth the Trip" />
    </main>
  );
}
